import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";                      // ← STATIC import, no dynamic import()
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { authHeaders } from "../../utils/authHeaders";
import sbeeLogo from "../../assets/sbeelogo.png";

// Turns "john.doe@sbee.in" or "john_doe" into "John" (first token, first letter capitalized)
const getDisplayNameFromEmail = (value) => {
  if (!value) return "";
  const namePart = String(value).split("@")[0];
  const firstToken = namePart.split(/[._\s]+/)[0] || "";
  if (!firstToken) return "";
  return firstToken.charAt(0).toUpperCase() + firstToken.slice(1).toLowerCase();
};

// Builds a single-line address string from a Location Master record.
// Tries a single full-address field first, then falls back to assembling
// whichever line/city/state/pincode-style fields exist, whatever they're called.
const buildUnit1Address = (loc) => {
  if (!loc) return "";

  const singleFieldCandidates = [
    "full_address", "address", "location_address", "registered_address", "address_full",
  ];
  for (const key of singleFieldCandidates) {
    if (loc[key] && String(loc[key]).trim()) return String(loc[key]).trim();
  }

  const pick = (...keys) => keys.map((k) => loc[k]).find((v) => v && String(v).trim());

  const parts = [
    pick("address_line1", "address1", "line1", "street", "street_address"),
    pick("address_line2", "address2", "line2", "area", "locality"),
    pick("city", "location_city", "town"),
    pick("state", "location_state"),
    pick("pincode", "pin_code", "zip", "zipcode", "postal_code"),
  ].filter(Boolean);

  if (parts.length) return parts.join(", ");

  // Last resort: concatenate any other short string-ish fields on the record,
  // skipping obvious non-address fields (ids, labels, timestamps, flags).
  const skip = new Set([
    "id", "location_id", "location_label", "created_at", "updated_at",
    "is_active", "status", "type", "location_type",
  ]);
  const fallback = Object.entries(loc)
    .filter(([k, v]) => !skip.has(k) && typeof v === "string" && v.trim() && v.length < 60)
    .map(([, v]) => v.trim());
  return fallback.join(", ");
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString();
};

const GatePassView = () => {
  const { gatePassNo } = useParams();
  const [searchParams] = useSearchParams();
  const [gpData, setGpData] = useState(null);
  const [statusMsg, setStatusMsg] = useState("Loading...");

  // Refs so PDF function always reads latest data (no stale closure)
  const gpRef     = useRef(null);
  const didTrigger = useRef(false); // prevent double-trigger

  // Unit 1 (Regd. Office) name/address, from Configuration > Location Master —
  // shown in the company header on both the on-screen slip and the PDF.
  const [unit1Location, setUnit1Location] = useState(null);
  const unit1Ref = useRef(null); // ref mirror so the PDF builder never reads a stale/empty value

  useEffect(() => { loadGatePass(); }, [gatePassNo]);

  const loadUnit1Location = async () => {
    try {
      const res = await fetch(`${MASTER_API}/location-master`, { headers: authHeaders() });
      const data = await res.json();
      const locations = Array.isArray(data) ? data : [];
      const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
      unit1Ref.current = unit1;
      setUnit1Location(unit1);
    } catch (err) {
      console.error("LOCATION MASTER FETCH ERROR", err);
      unit1Ref.current = null;
      setUnit1Location(null);
    }
  };

  const loadGatePass = async () => {
    try {
      const [res] = await Promise.all([
        fetch(`${PURCHASE_API}/gatepass/${gatePassNo}`, { headers: authHeaders() }),
        loadUnit1Location(),
      ]);
      const data = await res.json();

      if (res.status === 401) {
        setStatusMsg("Session expired or not signed in — please log in again");
        return;
      }

      if (!data?.success || !data?.gatePass) {
        setStatusMsg("Gate Pass not found");
        return;
      }

      gpRef.current = data.gatePass;
      setGpData(data.gatePass);
      setStatusMsg("ready");

      // Trigger action AFTER data is in refs
      if (!didTrigger.current) {
        didTrigger.current = true;
        const action = searchParams.get("action");
        if (action === "download") buildAndSavePDF(data.gatePass);
        else if (action === "print") setTimeout(() => window.print(), 400);
      }
    } catch (err) {
      console.error("Gate Pass load error:", err);
      setStatusMsg("Error: " + err.message);
    }
  };

  // ── Convert image src → base64 safely ──────────────────────────────────────
  const toBase64 = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width  = img.naturalWidth  || 400;
          c.height = img.naturalHeight || 120;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/png"));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = src.startsWith("data:") ? src : src + "?cb=" + Date.now();
    });

  // ── Core PDF builder ────────────────────────────────────────────────────────
  const buildAndSavePDF = async (gp) => {
    if (!gp) return;

    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const PW = 297, ML = 10, MR = 10, CW = PW - ML - MR;
    let y = 10;

    const guard = (need = 10) => { if (y + need > 190) { pdf.addPage(); y = 10; } };
    const hline = (x1, y1, x2, y2) => { pdf.setDrawColor(0); pdf.line(x1, y1, x2, y2); };
    const box   = (x, by, w, h)    => { pdf.setDrawColor(0); pdf.rect(x, by, w, h); };

    pdf.setTextColor(0, 0, 0);

    // ── Logo ─────────────────────────────────────────────────────────────────
    const logoB64 = await toBase64(sbeeLogo);
    if (logoB64) {
      const lw = 48;
      pdf.addImage(logoB64, "PNG", (PW - lw) / 2, y, lw, 14);
      y += 18;
    } else {
      pdf.setFontSize(18); pdf.setFont("helvetica", "bold");
      pdf.text("SBEE", PW / 2, y + 8, { align: "center" });
      y += 14;
    }

    // ── Title block — bordered, company name + form name, Date box on the right ──
    const unit1Addr = buildUnit1Address(unit1Ref.current);
    const titleH = unit1Addr ? 18 : 14, dateBoxW = 40;
    box(ML, y, CW, titleH);

    pdf.setFontSize(13); pdf.setFont("helvetica", "bold");
    pdf.text("SBEE CABLES (INDIA) LIMITED", ML + (CW - dateBoxW) / 2, y + 6, { align: "center" });

    let titleLineY = y + 12;
    if (unit1Addr) {
      pdf.setFontSize(7.5); pdf.setFont("helvetica", "normal");
      pdf.text(unit1Addr, ML + (CW - dateBoxW) / 2, y + 11, { align: "center" });
      titleLineY = y + 17;
    }

    pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
    pdf.text("INWARD GATE ENTRY", ML + (CW - dateBoxW) / 2, titleLineY, { align: "center" });

    pdf.setFontSize(9); pdf.setFont("helvetica", "bold");
    pdf.text("Date :", ML + CW - dateBoxW + 3, y + 8);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(fmtDate(gp.request_datetime) || ""), ML + CW - dateBoxW + 16, y + 8);

    y += titleH;

    // ── Single flat register table — same column order & wording as the paper form ──
    const cols = [
      { key: "sl",     label: "SL.NO",                       w: 9,  align: "center" },
      { key: "date",   label: "DATE",                        w: 16, align: "center" },
      { key: "veh",    label: "VEHICLE NO",                  w: 16, align: "center" },
      { key: "po",     label: "P.O Number",                  w: 24, align: "center" },
      { key: "dn",     label: "Delivery Note No",            w: 18, align: "center" },
      { key: "vendor", label: "Name of Vendor and address",  w: 54, align: "left"   },
      { key: "desc",   label: "Discription of Material",     w: 54, align: "left"   },
      { key: "pkg",    label: "No Of Pakage",                w: 14, align: "center" },
      { key: "qty",    label: "Qty as per Delivery Note",    w: 16, align: "center" },
      { key: "intime", label: "In Time",                     w: 12, align: "center" },
      { key: "secin",  label: "Security Name",                w: 16, align: "center" },
      { key: "outtime",label: "Out Time",                    w: 12, align: "center" },
      { key: "secout", label: "Security Name",                w: 16, align: "center" },
    ];
    const HH = 12, RH = 9;

    let cx = ML;
    pdf.setFontSize(6.8); pdf.setFont("helvetica", "bold");
    box(ML, y, CW, HH);
    cols.forEach((col) => {
      if (cx > ML) hline(cx, y, cx, y + HH);
      const lines = pdf.splitTextToSize(col.label, col.w - 2);
      const startTy = y + HH / 2 - ((lines.length - 1) * 2.6) / 2 + 2;
      lines.forEach((line, li) => {
        pdf.text(line, cx + col.w / 2, startTy + li * 2.6, { align: "center" });
      });
      cx += col.w;
    });
    y += HH;

    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5);
    const products = gp.products && gp.products.length ? gp.products : [{}];
    products.forEach((p, idx) => {
      guard(RH); box(ML, y, CW, RH);
      const cells = {
        sl: idx + 1,
        date: fmtDate(gp.request_datetime),
        veh: gp.vehicle_number,
        po: gp.reference_doc_no,
        dn: gp.delivery_note_no,
        vendor: gp.vendor_name_address,
        desc: p.product_description,
        pkg: p.number,
        qty: p.received_quantity,
        intime: gp.in_time,
        secin: gp.security_name_in,
        outtime: gp.out_time,
        secout: gp.security_name_out,
      };
      cx = ML;
      cols.forEach((col) => {
        if (cx > ML) hline(cx, y, cx, y + RH);
        const v = String(cells[col.key] ?? "-");
        const tx = col.align === "left" ? cx + 1.5 : cx + col.w / 2;
        const lines = pdf.splitTextToSize(v, col.w - 2.5);
        pdf.text(lines[0] ?? "", tx, y + 5.6, { align: col.align });
        cx += col.w;
      });
      y += RH;
    });

    // ── Blank signature space at bottom-left, matching the paper form ──
    y += 20; guard(20);
    hline(ML, y, ML + 40, y);

    pdf.save(`${gp.gate_pass_no}_GatePass.pdf`);
  };

  // Styles for the on-screen view
  const th  = { border: "1px solid black", padding: "5px 4px", textAlign: "center", fontWeight: "bold", fontSize: "11px", background: "#e6e6e6" };
  const td  = { border: "1px solid black", padding: "5px 4px", textAlign: "center", fontSize: "11px" };
  const tdL = { border: "1px solid black", padding: "5px 4px", textAlign: "left",   fontSize: "11px" };

  const products = gpData?.products && gpData.products.length ? gpData.products : [{}];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #gp-container, #gp-container * { visibility: visible; }
          #gp-container { position: absolute; left: 0; top: 0; width: 100%; }
          @page { size: A4 landscape; margin: 8mm; }
        }
      `}</style>

      <div
        id="gp-container"
        style={{ width: "1400px", margin: "20px auto", border: "1px solid black",
                 padding: "0", fontFamily: "Calibri,Arial,sans-serif", background: "#fff" }}
      >
        {!gpData ? (
          <div style={{ padding: 20, color: "#64748b" }}>{statusMsg}</div>
        ) : (
          <>
            {/* ── Logo ── */}
            <div style={{ textAlign: "center", padding: "10px 0 0" }}>
              <img src={sbeeLogo} alt="SBEE" style={{ width: "180px", height: "auto" }} />
            </div>

            {/* ── Title block — company name + form name, Date box on the right ── */}
            <div style={{ display: "flex", borderBottom: "1px solid black" }}>
              <div style={{ flex: 1, textAlign: "center", padding: "8px 0" }}>
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>SBEE CABLES (INDIA) LIMITED</div>
                {unit1Location && (
                  <div style={{ fontSize: "12px", marginTop: "2px" }}>
                    {buildUnit1Address(unit1Location)}
                  </div>
                )}
                <div style={{ fontSize: "15px", fontWeight: "bold", marginTop: "4px" }}>INWARD GATE ENTRY</div>
              </div>
              <div style={{ width: "150px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "bold" }}>
                Date : {fmtDate(gpData.request_datetime) || ""}
              </div>
            </div>

            {/* ── Single flat register table — same column order & wording as the paper form ── */}
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "3%" }}>SL.NO</th>
                  <th style={{ ...th, width: "6%" }}>DATE</th>
                  <th style={{ ...th, width: "6%" }}>VEHICLE NO</th>
                  <th style={{ ...th, width: "8%" }}>P.O Number</th>
                  <th style={{ ...th, width: "7%" }}>Delivery Note No</th>
                  <th style={{ ...th, width: "17%" }}>Name of Vendor and address</th>
                  <th style={{ ...th, width: "17%" }}>Discription of Material</th>
                  <th style={{ ...th, width: "6%" }}>No Of Pakage</th>
                  <th style={{ ...th, width: "7%" }}>Qty as per Delivery Note</th>
                  <th style={{ ...th, width: "5%" }}>In Time</th>
                  <th style={{ ...th, width: "6%" }}>Security Name</th>
                  <th style={{ ...th, width: "5%" }}>Out Time</th>
                  <th style={{ ...th, width: "6%" }}>Security Name</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, index) => (
                  <tr key={index}>
                    <td style={td}>{index + 1}</td>
                    <td style={td}>{fmtDate(gpData.request_datetime) || "-"}</td>
                    <td style={td}>{gpData.vehicle_number || "-"}</td>
                    <td style={td}>{gpData.reference_doc_no || "-"}</td>
                    <td style={td}>{gpData.delivery_note_no || "-"}</td>
                    <td style={tdL}>{gpData.vendor_name_address || "-"}</td>
                    <td style={tdL}>{p.product_description || "-"}</td>
                    <td style={td}>{p.number || "-"}</td>
                    <td style={td}>{p.received_quantity || "-"}</td>
                    <td style={td}>{gpData.in_time || "-"}</td>
                    <td style={td}>{gpData.security_name_in || "-"}</td>
                    <td style={td}>{gpData.out_time || "-"}</td>
                    <td style={td}>{gpData.security_name_out || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Blank signature space at bottom-left, matching the paper form ── */}
            <div style={{ height: "60px" }} />
            <div style={{ width: "180px", borderTop: "1px solid black", margin: "0 0 15px 15px" }} />
          </>
        )}
      </div>
    </>
  );
};

export default GatePassView;